













































































import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";

import DatePickerCompoment from "@/components/common/DatePicker.vue";
import TimePickerComponent from "@/components/common/TimePicker.vue";

import { firestore } from "firebase/app";
import { closeProgramChain } from "@/data/repository/PanelRepository";
import { ProgramChain } from "@/data/models/ProgramChain";
import { formatDateAndTime } from "@/utils/DateFormatter";
import {
  getAdjustedEndDate,
  getInitialMeterReading,
} from "@/data/models/RunOverview";
import { formatFloat } from "@/utils/LocalisationHelper";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "date-picker": DatePickerCompoment,
    "time-picker": TimePickerComponent,
  },
})
export default class SetPanelOfflineComponent extends Vue {
  serialNumber = "";
  selectedDate = firestore.Timestamp.fromDate(new Date());
  minDate = firestore.Timestamp.fromDate(new Date());
  resolve?: (value: boolean) => void = undefined;
  reject?: (reason: string) => void = undefined;

  endMeterReading = "";
  storedEndMeterReading = "";
  endMeterReadingHint: string | null = null;
  storedMeterReadingChecked = true;

  meterReadingByUser = "0";
  meterReadingByUserChecked = false;

  isMeterReadingInvalid = false;

  get saveDisabled() {
    if (this.isMeterReadingInvalid) {
      return true;
    }

    if (this.selectedDate.toMillis() < this.minDate.toMillis()) {
      return true;
    }

    return false;
  }

  @Watch("storedMeterReadingChecked")
  onStoredMeterReadingCheckedChanged(newValue: boolean) {
    this.isMeterReadingInvalid = !newValue && !this.meterReadingByUserChecked;

    if (newValue) {
      this.meterReadingByUserChecked = false;
    }
  }

  @Watch("meterReadingByUserChecked")
  onMeterReadingByUserCheckedChanged(newValue: boolean) {
    this.isMeterReadingInvalid = !newValue && !this.storedMeterReadingChecked;
    if (newValue) {
      this.storedMeterReadingChecked = false;
    }
  }

  @Ref("datePicker")
  datePicker!: DatePickerCompoment;

  @Ref("timePicker")
  timePicker!: TimePickerComponent;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  programChain: ProgramChain | null = null;

  @Watch("endMeterReading")
  onEndMeterReadingChanged(newValue: string) {
    const endMeterReading = parseFloat(newValue);

    if (newValue != this.storedEndMeterReading) {
      this.endMeterReadingHint = null;
    }

    this.isMeterReadingInvalid =
      endMeterReading < (this.startMeterReading ?? 0);
  }

  get datePickerStyle() {
    return "{ max-width: 0px; width: 0px; min-width: 0px }";
  }

  get startMeterReading() {
    if (!this.programChain) {
      return null;
    }

    const firstRun = this.programChain.runs[0];

    if (!firstRun) {
      return null;
    }

    return getInitialMeterReading(firstRun);
  }

  get formattedStartMeterReading() {
    return formatFloat(this.startMeterReading, 2);
  }

  get programChainDateText() {
    const programChain = this.programChain;

    if (!programChain) {
      return null;
    }

    return this.$t("panelDetail.actions.closeProgramChainText", [
      formatDateAndTime(programChain.startDate, programChain.startDate),
    ]);
  }

  onSelectedDate(newValue: firestore.Timestamp) {
    if (newValue) {
      this.timePicker.setNewInternalDate(null);
      this.$nextTick(() => {
        this.timePicker.textField.focus();
      });
    }
  }

  async show(serialNumber: string, programChain: ProgramChain) {
    this.programChain = programChain;
    this.isMeterReadingInvalid = false;
    this.serialNumber = serialNumber;
    this.storedMeterReadingChecked = true;
    this.meterReadingByUser = "";

    const lastRun = programChain.runs[programChain.runs.length - 1];

    this.selectedDate = firestore.Timestamp.fromDate(
      getAdjustedEndDate(lastRun, true)
    );

    this.storedEndMeterReading = formatFloat(
      lastRun.latestMeterReading ?? this.startMeterReading,
      2
    ).replace(",", ".");
    this.endMeterReading = this.storedEndMeterReading;

    this.minDate = this.selectedDate;
    this.endMeterReadingHint = this.$t("panelDetail.lastMeterReading", [
      formatDateAndTime(this.selectedDate, this.selectedDate),
    ]).toString();

    this.baseDialogComponent.show({
      title: this.$t("panelDetail.actions.endDrying").toString(),
      persistent: true,
      closeOnSave: false,
    });

    return new Promise<boolean>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  collectDate() {
    const dateStamp = this.datePicker.internal;

    if (!dateStamp) {
      return null;
    }

    const date = dateStamp.toDate();

    const timeStamp = this.timePicker.internal;

    if (!timeStamp) {
      date.setHours(0);
      date.setMinutes(0);

      return date;
    }

    const time = timeStamp.toDate();

    date.setHours(time.getHours());
    date.setMinutes(time.getMinutes());

    return date;
  }

  async onSave() {
    const date = this.collectDate();

    if (!date) {
      return;
    }

    const programChain = this.programChain;

    if (!programChain) {
      return;
    }

    let meterReadingEnd = parseFloat(this.endMeterReading);

    if (this.meterReadingByUserChecked) {
      meterReadingEnd = parseFloat(this.meterReadingByUser);

      if (meterReadingEnd < (this.startMeterReading ?? 0)) {
        return;
      }
    }

    this.baseDialogComponent.isShowing = false;

    await closeProgramChain(
      this.serialNumber,
      programChain,
      date,
      meterReadingEnd
    );
  }
}
